import { useCallback, useEffect, useState } from 'react';
import { Dropdown } from 'primereact/dropdown';
import { Calendar } from 'primereact/calendar';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { Combobox } from 'medere-ui-framework';
import PatientClinicalPanelById from '../../../components/PatientClinicalPanel/PatientClinicalPanel';
import {
  getStudyTypes,
  searchProfessionals,
} from '../../../services/StudyResultsController';
import type {
  StudyTypeOption,
  ProfessionalOption,
  StudyDetail,
} from '../../../services/StudyResultsController';

export interface StudyDataStepProps {
  patientId: number;
  studyDetail: StudyDetail | null;

  studyTypeId: number | null;
  onStudyTypeIdChange: (id: number | null) => void;
  serviceDate: Date | null;
  onServiceDateChange: (date: Date | null) => void;
  orderedById: number | null;
  orderedByOption: ProfessionalOption | null;
  onOrderedByChange: (option: ProfessionalOption | null) => void;
  externalId: string;
  onExternalIdChange: (value: string) => void;
  presumptiveDiagnostic: string;
  onPresumptiveDiagnosticChange: (value: string) => void;
}

export default function StudyDataStep({
  patientId,
  studyDetail,
  studyTypeId,
  onStudyTypeIdChange,
  serviceDate,
  onServiceDateChange,
  orderedByOption,
  onOrderedByChange,
  externalId,
  onExternalIdChange,
  presumptiveDiagnostic,
  onPresumptiveDiagnosticChange,
}: StudyDataStepProps) {
  const [studyTypes, setStudyTypes] = useState<StudyTypeOption[]>([]);

  useEffect(() => {
    getStudyTypes().then(setStudyTypes).catch(() => {});
  }, []);

  const handleProfessionalSearch = useCallback(
    (props: { filterValue?: string }) =>
      searchProfessionals(props.filterValue ?? '').then((items) => items ?? []),
    [],
  );

  const hasOrder =
    studyDetail &&
    (studyDetail.studyOrderId != null || studyDetail.studyOrderProfessional);

  return (
    <div className="study-data-step">
      {/* Patient clinical panel */}
      <div className="study-data-step__patient-panel">
        <PatientClinicalPanelById patientId={patientId} />
      </div>

      {/* Study order info banner */}
      {hasOrder ? (
        <div className="study-data-step__order-banner">
          <div className="study-data-step__order-title">
            <i className="pi pi-info-circle" />
            Orden de estudio asociada
          </div>
          <div className="study-data-step__order-detail">
            {studyDetail!.studyOrderProfessional ? (
              <>
                <strong>Solicitado por:</strong> {studyDetail!.studyOrderProfessional}
                {studyDetail!.studyOrderDate ? ` el ${studyDetail!.studyOrderDate}` : ''}
                <br />
              </>
            ) : null}
            {studyDetail!.studyOrderDiagnosis ? (
              <>
                <strong>Diagnóstico:</strong> {studyDetail!.studyOrderDiagnosis}
                <br />
              </>
            ) : null}
            {studyDetail!.studyOrderItems ? (
              <>
                <strong>Ítems:</strong> {studyDetail!.studyOrderItems}
              </>
            ) : null}
          </div>
        </div>
      ) : null}

      {/* Form fields */}
      <div className="study-data-step__form">
        {/* Study Type */}
        <div className="study-data-step__field">
          <label className="study-data-step__label">Tipo de estudio *</label>
          <Dropdown
            value={studyTypeId}
            options={studyTypes}
            optionLabel="name"
            optionValue="id"
            onChange={(e) => onStudyTypeIdChange(e.value)}
            placeholder="Seleccione tipo de estudio"
            className="study-data-step__dropdown"
            showClear
          />
        </div>

        {/* Service Date */}
        <div className="study-data-step__field">
          <label className="study-data-step__label">Fecha del estudio *</label>
          <Calendar
            value={serviceDate}
            onChange={(e) => onServiceDateChange(e.value as Date | null)}
            dateFormat="dd/mm/yy"
            showIcon
            placeholder="dd/mm/aaaa"
            className="study-data-step__calendar"
          />
        </div>

        {/* Ordered By */}
        <div className="study-data-step__field">
          <label className="study-data-step__label">Solicitado por</label>
          <Combobox
            value={orderedByOption}
            setValue={onOrderedByChange}
            getItems={handleProfessionalSearch}
            optionLabel="name"
            placeholder="Buscar profesional..."
            className="border2"
            style={{ width: '100%' }}
          />
        </div>

        {/* External ID */}
        <div className="study-data-step__field">
          <label className="study-data-step__label">ID Externo</label>
          <InputText
            value={externalId}
            onChange={(e) => onExternalIdChange(e.target.value)}
            placeholder="Código de referencia externo"
            className="study-data-step__input"
          />
        </div>

        {/* Presumptive Diagnostic */}
        <div className="study-data-step__field study-data-step__field--full">
          <label className="study-data-step__label">Diagnóstico presuntivo</label>
          <InputTextarea
            value={presumptiveDiagnostic}
            onChange={(e) => onPresumptiveDiagnosticChange(e.target.value)}
            rows={3}
            autoResize
            placeholder="Diagnóstico presuntivo..."
            className="study-data-step__textarea"
          />
        </div>
      </div>
    </div>
  );
}
